import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { twilioService } from '../../../services/twilioService';
import { useAuth } from '../../../contexts/AuthContext';

const SettingsPanel = ({ settings, onUpdateSettings, onThemeToggle }) => {
  const [activeSection, setActiveSection] = useState('general');
  const { user } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsPreferences, setSmsPreferences] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Load SMS preferences on mount
  React.useEffect(() => {
    if (user?.id && activeSection === 'notifications') {
      loadSMSPreferences();
    }
  }, [user?.id, activeSection]);

  const loadSMSPreferences = async () => {
    try {
      const { data, error } = await twilioService?.getSMSPreferences(user?.id);
      if (error) throw new Error(error);
      setSmsPreferences(data);
    } catch (err) {
      console.error('Error loading SMS preferences:', err);
    }
  };

  const handleUpdatePhoneNumber = async () => {
    if (!phoneNumber?.trim()) {
      setError('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { data, error } = await twilioService?.updatePhoneNumber(
        user?.id,
        phoneNumber,
        false // Phone not verified yet
      );

      if (error) throw new Error(error);

      setSuccess('Phone number updated successfully. Verification required to enable SMS.');
      setPhoneNumber('');
    } catch (err) {
      setError(err?.message || 'Failed to update phone number');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSMSPreference = async (preferenceKey, value) => {
    try {
      const { data, error } = await twilioService?.updateSMSPreferences(user?.id, {
        [preferenceKey]: value
      });

      if (error) throw new Error(error);

      setSmsPreferences(data);
    } catch (err) {
      console.error('Error updating SMS preference:', err);
      setError(err?.message || 'Failed to update preference');
    }
  };

  const sections = [
    { id: 'general', name: 'General', icon: 'Settings' },
    { id: 'notifications', name: 'Notifications', icon: 'Bell' },
    { id: 'sms', name: 'SMS Alerts', icon: 'MessageSquare' },
    { id: 'privacy', name: 'Privacy', icon: 'Shield' },
    { id: 'collections', name: 'Collections', icon: 'Package' },
    { id: 'connected', name: 'Connected Accounts', icon: 'Link' },
    { id: 'data', name: 'Data & Export', icon: 'Download' }
  ];

  const collectibleTypes = [
    { id: 'coins', name: 'Coins & Currency', description: 'Rare coins, banknotes, and numismatic items' },
    { id: 'stamps', name: 'Stamps', description: 'Philatelic collections and postal history' },
    { id: 'cards', name: 'Trading Cards', description: 'Sports cards, TCG, and collectible cards' },
    { id: 'comics', name: 'Comics & Books', description: 'Comic books, graphic novels, and rare books' },
    { id: 'art', name: 'Art & Antiques', description: 'Fine art, sculptures, and antique items' },
    { id: 'toys', name: 'Toys & Figures', description: 'Action figures, vintage toys, and collectibles' },
    { id: 'watches', name: 'Watches & Jewelry', description: 'Luxury timepieces and fine jewelry' },
    { id: 'memorabilia', name: 'Memorabilia', description: 'Sports, entertainment, and historical items' }
  ];

  const connectedAccounts = [
    { id: 'ebay', name: 'eBay', icon: 'ShoppingBag', connected: true, description: 'Sync auction data and market prices' },
    { id: 'heritage', name: 'Heritage Auctions', icon: 'Gavel', connected: false, description: 'Track auction results and estimates' },
    { id: 'comc', name: 'COMC', icon: 'CreditCard', connected: true, description: 'Import card collections and values' },
    { id: 'facebook', name: 'Facebook', icon: 'Facebook', connected: false, description: 'Share achievements with friends' },
    { id: 'instagram', name: 'Instagram', icon: 'Instagram', connected: true, description: 'Showcase your collections' }
  ];

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">General Preferences</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
            <div className="flex items-center space-x-3">
              <Icon name={settings?.theme === 'dark' ? 'Moon' : 'Sun'} size={20} />
              <div>
                <div className="font-medium text-foreground">Theme</div>
                <div className="text-sm text-muted-foreground">
                  Currently using {settings?.theme} mode
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onThemeToggle}
              iconName={settings?.theme === 'dark' ? 'Sun' : 'Moon'}
            >
              Switch to {settings?.theme === 'dark' ? 'Light' : 'Dark'}
            </Button>
          </div>

          <Input
            label="Display Name"
            type="text"
            value={settings?.displayName}
            onChange={(e) => onUpdateSettings({ displayName: e?.target?.value })}
            description="This name will be visible to other collectors"
          />

          <Input
            label="Time Zone"
            type="text"
            value={settings?.timezone}
            onChange={(e) => onUpdateSettings({ timezone: e?.target?.value })}
            description="Used for auction times and notifications"
          />

          <Input
            label="Currency"
            type="text"
            value={settings?.currency}
            onChange={(e) => onUpdateSettings({ currency: e?.target?.value })}
            description="Default currency for valuations"
          />
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Notification Preferences</h3>
        <div className="space-y-4">
          <div className="space-y-3">
            <Checkbox
              label="Email Notifications"
              description="Receive updates via email"
              checked={settings?.notifications?.email}
              onChange={(e) => onUpdateSettings({
                notifications: { ...settings?.notifications, email: e?.target?.checked }
              })}
            />
            <Checkbox
              label="Push Notifications"
              description="Browser and mobile push notifications"
              checked={settings?.notifications?.push}
              onChange={(e) => onUpdateSettings({
                notifications: { ...settings?.notifications, push: e?.target?.checked }
              })}
            />
            <Checkbox
              label="Market Alerts"
              description="Price changes and market opportunities"
              checked={settings?.notifications?.marketAlerts}
              onChange={(e) => onUpdateSettings({
                notifications: { ...settings?.notifications, marketAlerts: e?.target?.checked }
              })}
            />
            <Checkbox
              label="Auction Reminders"
              description="Upcoming auctions for your interests"
              checked={settings?.notifications?.auctionReminders}
              onChange={(e) => onUpdateSettings({
                notifications: { ...settings?.notifications, auctionReminders: e?.target?.checked }
              })}
            />
            <Checkbox
              label="Achievement Notifications"
              description="Collection milestones and achievements"
              checked={settings?.notifications?.achievements}
              onChange={(e) => onUpdateSettings({
                notifications: { ...settings?.notifications, achievements: e?.target?.checked }
              })}
            />
            <Checkbox
              label="Community Updates"
              description="Featured collections and community news"
              checked={settings?.notifications?.community}
              onChange={(e) => onUpdateSettings({
                notifications: { ...settings?.notifications, community: e?.target?.checked }
              })}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderSMSSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">SMS Notifications</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Get instant alerts for critical marketplace events via text message
        </p>

        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-600 text-sm">
            {success}
          </div>
        )}

        <div className="space-y-4">
          {/* Phone Number Management */}
          <div className="p-4 bg-muted/20 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-foreground">Phone Number</div>
                <div className="text-sm text-muted-foreground">
                  {settings?.phoneNumber ? (
                    <span>
                      {settings?.phoneNumber}
                      {settings?.phoneVerified ? (
                        <span className="ml-2 text-green-600">✓ Verified</span>
                      ) : (
                        <span className="ml-2 text-amber-600">⚠ Not Verified</span>
                      )}
                    </span>
                  ) : (
                    'No phone number added'
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Input
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e?.target?.value)}
                className="flex-1"
              />
              <Button
                onClick={handleUpdatePhoneNumber}
                disabled={loading || !phoneNumber?.trim()}
                size="sm"
              >
                {loading ? 'Updating...' : 'Update'}
              </Button>
            </div>

            {!settings?.phoneVerified && settings?.phoneNumber && (
              <div className="text-xs text-muted-foreground">
                Note: Phone verification required to receive SMS notifications. Contact support to verify your number.
              </div>
            )}
          </div>

          {/* SMS Notification Toggles */}
          {settings?.phoneVerified && (
            <div className="space-y-3">
              <div className="font-medium text-foreground">Marketplace Alerts</div>

              <Checkbox
                label="Offer Alerts"
                description="Get notified when you receive, accept, or reject offers"
                checked={smsPreferences?.offer_alerts !== false}
                onChange={(checked) => handleUpdateSMSPreference('offer_alerts', checked)}
              />

              <Checkbox
                label="Escrow Alerts"
                description="Payment received and funds released notifications"
                checked={smsPreferences?.escrow_alerts !== false}
                onChange={(checked) => handleUpdateSMSPreference('escrow_alerts', checked)}
              />

              <Checkbox
                label="Dispute Alerts"
                description="Critical alerts when disputes are opened or resolved"
                checked={smsPreferences?.dispute_alerts !== false}
                onChange={(checked) => handleUpdateSMSPreference('dispute_alerts', checked)}
              />

              <Checkbox
                label="Transaction Alerts"
                description="Listing sold and transaction completed notifications"
                checked={smsPreferences?.transaction_alerts !== false}
                onChange={(checked) => handleUpdateSMSPreference('transaction_alerts', checked)}
              />
            </div>
          )}

          {/* Info Box */}
          <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <div className="flex gap-3">
              <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-600">
                <div className="font-medium mb-1">About SMS Notifications</div>
                <div className="text-blue-600/80">
                  SMS alerts are sent for urgent marketplace events only. Standard messaging rates may apply.
                  You can disable specific alert types at any time.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPrivacySettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Privacy & Visibility</h3>
        <div className="space-y-4">
          <Checkbox
            label="Public Profile"
            description="Allow other collectors to view your profile"
            checked={settings?.privacy?.publicProfile}
            onChange={(e) => onUpdateSettings({
              privacy: { ...settings?.privacy, publicProfile: e?.target?.checked }
            })}
          />
          <Checkbox
            label="Show Collection Value"
            description="Display portfolio value on public profile"
            checked={settings?.privacy?.showValue}
            onChange={(e) => onUpdateSettings({
              privacy: { ...settings?.privacy, showValue: e?.target?.checked }
            })}
          />
          <Checkbox
            label="Allow Collection Sharing"
            description="Enable sharing individual items or collections"
            checked={settings?.privacy?.allowSharing}
            onChange={(e) => onUpdateSettings({
              privacy: { ...settings?.privacy, allowSharing: e?.target?.checked }
            })}
          />
          <Checkbox
            label="Show in Leaderboards"
            description="Include your achievements in community rankings"
            checked={settings?.privacy?.showInLeaderboards}
            onChange={(e) => onUpdateSettings({
              privacy: { ...settings?.privacy, showInLeaderboards: e?.target?.checked }
            })}
          />
        </div>
      </div>
    </div>
  );

  const renderCollectionSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Collection Preferences</h3>
        <p className="text-muted-foreground mb-4">
          Select your collecting interests to personalize your experience
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {collectibleTypes?.map((type) => (
            <div key={type?.id} className="p-4 border border-border rounded-lg">
              <Checkbox
                label={type?.name}
                description={type?.description}
                checked={settings?.collectibleTypes?.includes(type?.id)}
                onChange={(e) => {
                  const updatedTypes = e?.target?.checked
                    ? [...settings?.collectibleTypes, type?.id]
                    : settings?.collectibleTypes?.filter(t => t !== type?.id);
                  onUpdateSettings({ collectibleTypes: updatedTypes });
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderConnectedAccounts = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Connected Accounts</h3>
        <p className="text-muted-foreground mb-4">
          Connect your accounts to sync data and enhance your collecting experience
        </p>
        <div className="space-y-4">
          {connectedAccounts?.map((account) => (
            <div key={account?.id} className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name={account?.icon} size={24} />
                <div>
                  <div className="font-medium text-foreground">{account?.name}</div>
                  <div className="text-sm text-muted-foreground">{account?.description}</div>
                </div>
              </div>
              <Button
                variant={account?.connected ? "destructive" : "default"}
                size="sm"
              >
                {account?.connected ? "Disconnect" : "Connect"}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderDataSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Data Management</h3>
        <div className="space-y-4">
          <div className="p-4 bg-muted/20 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-foreground">Export Collection Data</div>
              <Button variant="outline" size="sm" iconName="Download">
                Export CSV
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Download your complete collection data including items, values, and history
            </p>
          </div>

          <div className="p-4 bg-muted/20 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-foreground">Backup Collection</div>
              <Button variant="outline" size="sm" iconName="Archive">
                Create Backup
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Create a complete backup of your collection including images
            </p>
          </div>

          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-destructive">Delete Account</div>
              <Button variant="destructive" size="sm" iconName="Trash2">
                Delete Account
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Permanently delete your account and all associated data
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'general': return renderGeneralSettings();
      case 'notifications': return renderNotificationSettings();
      case 'sms': return renderSMSSettings();
      case 'privacy': return renderPrivacySettings();
      case 'collections': return renderCollectionSettings();
      case 'connected': return renderConnectedAccounts();
      case 'data': return renderDataSettings();
      default: return renderGeneralSettings();
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      {/* Sidebar */}
      <div className="flex">
        <div className="w-64 border-r border-border bg-muted/20">
          <div className="p-4">
            <h2 className="text-lg font-semibold text-foreground mb-4">Settings</h2>
            <nav className="space-y-1">
              {sections?.map((section) => (
                <button
                  key={section?.id}
                  onClick={() => setActiveSection(section?.id)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                    activeSection === section?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground'
                  }`}
                >
                  <Icon name={section?.icon} size={18} />
                  <span className="text-sm font-medium">{section?.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          {activeSection === 'general' && renderGeneralSettings()}
          {activeSection === 'notifications' && renderNotificationSettings()}
          {activeSection === 'sms' && renderSMSSettings()}
          {activeSection === 'privacy' && renderPrivacySettings()}
          {activeSection === 'collections' && renderCollectionSettings()}
          {activeSection === 'connected' && renderConnectedAccounts()}
          {activeSection === 'data' && renderDataSettings()}
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;